import { useUserContext } from '@/contexts/UserContext';
import Modal, { ModalActionData } from '@superfunnel/ui-core/Modal';
import FieldTitle from '@superfunnel/ui-core/v2/FieldTitle';
import Input from '@superfunnel/ui-core/v2/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import Textarea from '@superfunnel/ui-core/v2/TextArea';
import { useCallback, useMemo, useState } from 'react';
import { createCustomPlan } from '../../../services/BillingService';
import {
  formValuesToCustomLimits,
  getDefaultLimitValues,
  PlanLimitsForm,
  PlanLimitsFormValues,
} from './PlanLimitsForm';

interface CreateCustomPlanModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateCustomPlanModal({
  isOpen,
  onClose,
}: CreateCustomPlanModalProps) {
  const { selectedAccount } = useUserContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();

  const [formData, setFormData] = useState({
    planName: '',
    billingInterval: 'month' as 'month' | 'year',
    price: '',
    currency: 'USD',
    startDate: '',
    endDate: '',
    contractId: '',
    notes: '',
  });

  const [limitsFormValues, setLimitsFormValues] =
    useState<PlanLimitsFormValues>(getDefaultLimitValues());

  const handleSubmit = useCallback(async () => {
    if (!selectedAccount) {
      setError('No account selected');
      return;
    }

    setIsSubmitting(true);
    setError(undefined);

    try {
      const accountId = selectedAccount.id;

      // Build custom limits from form component
      const customLimits = formValuesToCustomLimits(limitsFormValues);

      await createCustomPlan({
        accountId,
        planType: 'custom',
        planName: formData.planName,
        billingInterval: formData.billingInterval,
        price: Number(formData.price) * 100, // Convert to cents
        currency: formData.currency,
        customLimits,
        startDate: formData.startDate || undefined,
        endDate: formData.endDate || undefined,
        contractId: formData.contractId || undefined,
        notes: formData.notes || undefined,
      });

      alert('Custom plan created successfully!');
      onClose();

      // Reset form
      setFormData({
        planName: '',
        billingInterval: 'month',
        price: '',
        currency: 'USD',
        startDate: '',
        endDate: '',
        contractId: '',
        notes: '',
      });
      setLimitsFormValues(getDefaultLimitValues());

      // Reload page to show updated subscription
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      console.error('Failed to create custom plan:', error);
      setError(error.message || 'Failed to create custom plan');
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedAccount, formData, limitsFormValues, onClose]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: isSubmitting,
        onClick: onClose,
      },
      {
        title: 'Create Custom Plan',
        type: 'primary',
        loading: isSubmitting,
        onClick: handleSubmit,
      },
    ],
    [isSubmitting, onClose, handleSubmit],
  );

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      actions={actions}
      width="2xl"
      title="Create Custom Plan"
    >
      <div className="space-y-6">
        {/* Basic Plan Info */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Plan Details</h3>

          <div>
            <Input
              id="planName"
              title="Plan Name *"
              value={formData.planName}
              onChange={(e) =>
                setFormData({ ...formData, planName: e.target.value })
              }
              placeholder="Enterprise Plan"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <FieldTitle
                htmlFor="billingInterval"
                title="Billing Interval *"
              />
              <Select
                value={formData.billingInterval}
                onValueChange={(value: 'month' | 'year') =>
                  setFormData({ ...formData, billingInterval: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Monthly</SelectItem>
                  <SelectItem value="year">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Input
                id="price"
                title="Price (in dollars) *"
                type="number"
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
                placeholder="499"
                required
              />
            </div>
          </div>
        </div>

        {/* Custom Limits */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Custom Limits</h3>
          <p className="text-xs text-gray-500">
            Set limits for this plan. Check &quot;Unlimited&quot; for no limit.
          </p>

          <PlanLimitsForm
            values={limitsFormValues}
            onChange={setLimitsFormValues}
            disabled={isSubmitting}
            showBuffer={false}
          />
        </div>

        {/* Dates */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Billing Period</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                id="startDate"
                title="Start Date (optional)"
                type="datetime-local"
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
              />
            </div>

            <div>
              <Input
                id="endDate"
                title="End Date (optional)"
                type="datetime-local"
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        {/* Metadata */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold">Additional Information</h3>

          <div>
            <Input
              id="contractId"
              title="Contract ID (optional)"
              value={formData.contractId}
              onChange={(e) =>
                setFormData({ ...formData, contractId: e.target.value })
              }
              placeholder="CONTRACT-2025-001"
            />
          </div>

          <div>
            <Textarea
              id="notes"
              title="Notes (optional)"
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              placeholder="Any additional notes about this custom plan..."
              rows={3}
            />
          </div>
        </div>

        {error && (
          <div className="mt-4 w-full text-end text-red-600">{error}</div>
        )}
      </div>
    </Modal>
  );
}
